import { InventoryPool } from "@deliverr/business-types";
import ProductChooser, { SelectedProductData } from "common/components/ProductChooser";
import React, { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { isDestinationFBA } from "../utils/isDestinationFBA";
import { ReplenishmentWarehouseSelect } from "../replenishment/warehouse-selection/ReplenishmentWarehouseSelect";
import { isEmpty } from "lodash";
import { Button } from "common/components/ui";
import { BULK_UPLOAD_CONTENT_MESSAGES } from "components/BulkUpload/BulkUploadContentMessages";
import { ATPDetails } from "common/clients/inventory/ATP/ATPDetails";
import { BulkProductUploadModal } from "./bulk-product-upload/BulkProductUploadModal";
import { ReplenishmentConstants } from "transfers/constants/ReplenishmentConstants";
import { DistributionChannel } from "@deliverr/replenishment-client";

export interface TransferProductChooserProps {
  addProduct: (dsku: string, product: any) => void;
  removeProduct: (dsku: string, itemIndex?: number, selectedProduct?: SelectedProductData) => void;
  updateQty: (dsku: string, totalQuantity: number) => void;
  updateNumberOfCases: (dsku: string, numberOfCases: number) => void;
  selectedProducts: SelectedProductData[];
  shouldUseCasePack: boolean;
  distributionChannel: DistributionChannel;
  inventoryPool: InventoryPool;
  isKittingEnabled?: boolean;
  shouldShowLotNumberDropdown?: boolean;
  updateLotNumber?: (dsku: string, lotNumber?: string) => void;
  selectedProductsLotNumbersList?: {
    [dsku: string]: ATPDetails[] | undefined;
  };
  selectedProductsLotNumbers?: {
    [dsku: string]: ATPDetails | undefined;
  };
  onProductRowClick?: (product: SelectedProductData) => void;
}

export const TransferProductChooser: FC<TransferProductChooserProps> = ({
  addProduct,
  removeProduct,
  updateQty,
  updateNumberOfCases,
  selectedProducts,
  shouldUseCasePack,
  distributionChannel,
  inventoryPool,
  isKittingEnabled,
  shouldShowLotNumberDropdown,
  updateLotNumber,
  selectedProductsLotNumbersList,
  selectedProductsLotNumbers,
  onProductRowClick,
}) => {
  const [shouldShowBulkUploadModal, setShouldShowBulkUploadModal] = useState(false);

  const bulkUploadButton = (
    <Button secondary onClick={() => setShouldShowBulkUploadModal(true)} data-testid="product-upload-button">
      <FormattedMessage {...BULK_UPLOAD_CONTENT_MESSAGES.UPLOAD_FILE} />
    </Button>
  );

  return (
    <>
      <ProductChooser
        addProduct={addProduct}
        removeProduct={removeProduct}
        selectedProducts={selectedProducts}
        updateQty={updateQty}
        updateNumberOfCases={updateNumberOfCases}
        useCasePack={shouldUseCasePack}
        showOnlyPacks={inventoryPool === InventoryPool.STORAGE}
        isReserveStorage={inventoryPool === InventoryPool.STORAGE}
        shouldHideCreateProduct
        shouldShowAvailability
        shouldHideFastTagAlert
        shouldHideBatteryColumn
        withWalmartChannelIdOnly={distributionChannel === DistributionChannel.WFS}
        applyFbaConditions={isDestinationFBA(distributionChannel)}
        isIntegratedFBA={distributionChannel === DistributionChannel.FBA_V3}
        productLimit={ReplenishmentConstants.MAX_PRODUCT_LIMIT}
        shouldShowEcomDetailsForStorageProducts={ReplenishmentConstants.ECOM_CHANNELS.includes(distributionChannel)}
        shouldShowKitHint={isKittingEnabled && ReplenishmentConstants.ECOM_CHANNELS.includes(distributionChannel)}
        notificationComponent={
          !isEmpty(selectedProducts) ? (
            <ReplenishmentWarehouseSelect shouldAddProductInventory={false} inventoryPool={inventoryPool} />
          ) : null
        }
        allowMultiPacksSelection={distributionChannel === DistributionChannel.WHOLESALE}
        isCaseQtyReadonly
        hasMaxValue
        shouldShowLotNumberDropdown={shouldShowLotNumberDropdown}
        bulkUploadButton={bulkUploadButton}
        shouldShowRowNumber
        updateLotNumber={updateLotNumber}
        selectedProductsLotNumbersList={selectedProductsLotNumbersList}
        selectedProductsLotNumbers={selectedProductsLotNumbers}
        shouldShowParentPack
        onProductRowClick={onProductRowClick}
      />
      {shouldShowBulkUploadModal && <BulkProductUploadModal onClose={() => setShouldShowBulkUploadModal(false)} />}
    </>
  );
};
